/* eslint-disable max-len */
// @flow
import * as React from 'react';
import classNames from 'classnames';
import _ from 'lodash';

import { type EnhancedProps, withTransition } from 'client/components/common/HOCs/with-transition';
import { CAN_USE_DOM } from 'common/constants';
import { EVENT_TYPES, PART_TITLES } from 'client/constants/common';
import {
	ArticlePreview,
	Scoreboard,
	Timeline,
	Article,
	Title,
	Penalties,
	Tactic,
	AuditorsOpinion,
	OneLogo,
} from 'client/components/common/ContentComponents';
import { Responsive, GoogleAds } from 'client/components/common';
import { isIOS } from 'client/components/common/Responsive';
import * as css from './Intro.scss';

type Props = {|
	...EnhancedProps,
	in: boolean,
	game: any,
|};

type State = {};

class Intro extends React.PureComponent<Props, State> {
	transitionCompleteTimeout: TimeoutID;

	static defaultProps = {};

	componentDidMount() {
		const { onTransitionComplete } = this.props;
		this.transitionCompleteTimeout = setTimeout(onTransitionComplete, parseInt(css.transitionIn, 10));

		// Taboola implementation
		if (CAN_USE_DOM) {
			const script = document.createElement('script');
			const PUBLISHER_ID = Responsive.isMatching(Responsive.DESKTOP) ? 'ynet-ynet-' : 'ynet-ynetwebsite-mobile';
			script.textContent = `window._taboola = window._taboola || [];
				_taboola.push({article:'auto'});
				!function (e, f, u, i) {
					if (!document.getElementById(i)){
						e.async = 1;
						e.src = u;
						e.id = i;
						f.parentNode.insertBefore(e, f);
					}
				}(document.createElement('script'),
				document.getElementsByTagName('script')[0], '//cdn.taboola.com/libtrc/${PUBLISHER_ID}/loader.js', 'tb_loader_script');
				if(window.performance && typeof window.performance.mark == 'function'){
					window.performance.mark('tbl_ic');
				}`;

			if (document.head) {
				document.head.appendChild(script);
			}

			const taboola = window._taboola || [];
			if (Responsive.isMatching(Responsive.DESKTOP)) {
				taboola.push({
					mode: 'thumbnails-z-nd',
					container: 'taboola-left-rail-thumbnails-nd',
					placement: 'Left Rail Thumbnails ND',
					target_type: 'mix',
				});
				taboola.push({
					mode: 'alternating-thumbnails-a-nd',
					container: 'taboola-below-article-thumbnails-wide-nd',
					placement: 'Below Article Thumbnails Wide ND',
					target_type: 'mix',
				});
			} else {
				taboola.push({
					mode: 'alternating-thumbnails-mid-nd-',
					container: 'taboola-mobile-below-article-thumbnails-nd',
					placement: 'Mobile Below Article Thumbnails ND',
					target_type: 'mix',
				});
			}
		}
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.in !== prevProps.in) {
			const { onTransitionComplete } = this.props;
			let duration = parseInt(this.props.in ? css.transitionIn : css.transitionOut, 10);

			if (!this.props.in) {
				duration = 0;
			}
			clearTimeout(this.transitionCompleteTimeout);
			this.transitionCompleteTimeout = setTimeout(onTransitionComplete, duration);
		}
	}

	render() {
		const { transitionState, game } = this.props;
		const penaltiesIndex = _.findIndex(_.get(game, 'Arena.ArenaEvents', []), {
			Event: EVENT_TYPES.MATCH_STATUS,
			Text: PART_TITLES.PENALTIES,
		});

		const homeTeamNew = _.get(game, 'Arena.HomeTeam', '');
		const guestTeamNew = _.get(game, 'Arena.GuestTeam', '');
		const homeTeamLogo = _.get(game, 'Arena.HomeLogo', '');
		const guestTeamLogo = _.get(game, 'Arena.GuestLogo', '');
		// for old API versions
		const homeTeamID = _.last(homeTeamLogo.split('/')).replace('.gif', '');
		const guestTeamID = _.last(guestTeamLogo.split('/')).replace('.gif', '');
		const teams = _.get(game, 'Affinities.Teams', []);
		const oldHomeTeam = _.get(_.find(teams, { id: Number(homeTeamID) }), 'Name', '');
		const oldGuestTeam = _.get(_.find(teams, { id: Number(guestTeamID) }), 'Name', '');

		const homeTeam = homeTeamNew || oldHomeTeam;
		const guestTeam = guestTeamNew || oldGuestTeam;

		return (
			<>
				<div className={classNames(css.intro, css[transitionState])}>
					{CAN_USE_DOM && (
						<Responsive query={[Responsive.DESKTOP]}>
							<GoogleAds
								id="/6870/ynet/desktop/top/sport"
								path="/6870/ynet/desktop/top/sport"
								size={[1192, 130]}
							/>
							<iframe
								src="https://z.ynet.co.il/short/commerce/2021/links/header/"
								title="header"
								style={{ border: '0', width: '100%', display: 'block', margin: '0 auto' }}
							></iframe>
						</Responsive>
					)}
					<OneLogo />
					<ArticlePreview
						title={_.get(game, 'Title', '')}
						description={_.get(game, 'Description', '')}
						imageInfo={_.find(_.get(game, 'InnerObjects', {}), { ID: 0 })}
						author={_.get(game, 'Credit', '')}
						date={_.get(game, 'Arena.Date', '')}
					/>
					<Scoreboard
						stadium={_.get(game, 'Arena.Stadium', '')}
						stadiumVisitors={_.get(game, 'Arena.StadiumVisitors', '')}
						league={`${_.get(game, 'Arena.League', '')} - ${_.get(game, 'Arena.RoundName', '')}`}
						date={_.get(game, 'Arena.Date', '')}
						homeTeam={homeTeam}
						homeTeamLogo={homeTeamLogo}
						homeScore={_.get(game, 'Arena.HomeScore', 0)}
						guestTeam={guestTeam}
						guestTeamLogo={guestTeamLogo}
						questScore={_.get(game, 'Arena.GuestScore', 0)}
						refereeName={_.get(game, 'Arena.RefereeName', '')}
						refereeGrade={_.get(game, 'Arena.RefereeGrade', 0)}
						goalArenaEvents={_.filter(
							_.get(game, 'Arena.ArenaEvents', []),
							event =>
								event.Event === EVENT_TYPES.GOAL ||
								(event.Event === EVENT_TYPES.PENALTY_GOAL && event.Minute) ||
								event.Event === EVENT_TYPES.OWN_GOAL
						)}
					/>
					<div className={css.mainContent}>
						<main>
							{penaltiesIndex > 0 && (
								<Penalties
									title="קרב פנדלים"
									homeTeam={homeTeam}
									guestTeam={guestTeam}
									teamsLogo={{
										[`${homeTeam}`]: homeTeamLogo,
										[`${guestTeam}`]: guestTeamLogo,
									}}
									penalties={_.get(game, 'Arena.ArenaEvents', []).slice(penaltiesIndex + 1)}
								/>
							)}
							{_.get(game, 'Arena.MVP', '') && _.get(game, 'Arena.LVP', '') && (
								<AuditorsOpinion
									title="דעת המבקר"
									bestPlayer={
										_.find(_.get(game, 'Arena.HomeLineUp', []), {
											ID: _.get(game, 'Arena.MVP', ''),
										}) ||
										_.find(_.get(game, 'Arena.GuestLineUp', []), {
											ID: _.get(game, 'Arena.MVP', ''),
										})
									}
									worstPlayer={
										_.find(_.get(game, 'Arena.HomeLineUp', []), {
											ID: _.get(game, 'Arena.LVP', ''),
										}) ||
										_.find(_.get(game, 'Arena.GuestLineUp', []), {
											ID: _.get(game, 'Arena.LVP', ''),
										})
									}
								/>
							)}
							<Title title="הרכבים וציונים" className={css.arenaEventsTitle} />
							<Tactic
								homeLineUp={_.get(game, 'Arena.HomeLineUp', [])}
								homeTactic={_.get(game, 'Arena.HomeTactic', '')}
								homeColor={_.get(game, 'Arena.HomeColor', 0)}
								guestLineUp={_.get(game, 'Arena.GuestLineUp', [])}
								guestTactic={_.get(game, 'Arena.GuestTactic', '')}
								guestColor={_.get(game, 'Arena.GuestColor', 0)}
								cardsEvents={_.filter(
									_.get(game, 'Arena.ArenaEvents', []),
									event =>
										event.Event === EVENT_TYPES.YELLOW_CARD || event.Event === EVENT_TYPES.RED_CARD
								)}
								playersOutEvents={_.filter(
									_.get(game, 'Arena.ArenaEvents', []),
									event => event.PlayerOut
								)}
							/>
							<Article text={_.get(game, 'Text', '')} objects={_.get(game, 'InnerObjects', [])} />
							<Timeline
								timeline={_.get(game, 'Arena.ArenaEvents', [])}
								teamsLogo={{
									[`${homeTeam}`]: homeTeamLogo,
									[`${guestTeam}`]: guestTeamLogo,
								}}
							/>

							<div className={css.widget}>
								<Responsive query={[Responsive.DESKTOP]}>
									<div id="taboola-below-article-thumbnails-wide-nd"></div>
								</Responsive>
								<Responsive query={[Responsive.MOBILE, Responsive.TABLET]}>
									<div id="taboola-mobile-below-article-thumbnails-nd"></div>
								</Responsive>
							</div>
						</main>
						<Responsive query={[Responsive.DESKTOP]}>
							<aside>
								<GoogleAds
									id="/6870/ynet/desktop/box.1/sport"
									path="/6870/ynet/desktop/box.1/sport"
									size={[300, 250]}
									className={css.googleAds}
								/>

								<div id="taboola-left-rail-thumbnails-nd"></div>

								<GoogleAds
									id="/6870/ynet/desktop/box.2/sport"
									path="/6870/ynet/desktop/box.2/sport"
									size={[300, 250]}
									className={css.googleAds}
								/>
								<GoogleAds
									id="/6870/ynet/desktop/box.3/sport"
									path="/6870/ynet/desktop/box.3/sport"
									size={[300, 250]}
									className={css.googleAds}
								/>
							</aside>
						</Responsive>
					</div>
					<Responsive query={[Responsive.MOBILE, Responsive.TABLET]}>
						<GoogleAds
							id={
								isIOS()
									? '/6870/ynet/mobileweb_iphone/banner/sport'
									: '/6870/ynet/mobileweb_android/banner/sport'
							}
							path={
								isIOS()
									? '/6870/ynet/mobileweb_iphone/banner/sport'
									: '/6870/ynet/mobileweb_android/banner/sport'
							}
							size={[360, 50]}
							className={css.googleAdsMobileStiky}
						/>
					</Responsive>
				</div>
				<Responsive query={[Responsive.DESKTOP]}>
					<div className={css.googleAdsRightTop}>
						<GoogleAds
							id="/6870/ynet/desktop/skyscraper/sport"
							path="/6870/ynet/desktop/skyscraper/sport"
							size={[160, 600]}
						/>
					</div>
				</Responsive>
			</>
		);
	}
}

export default withTransition(Intro);
